/* Optimizer: the settings for the module that proposes technician changes.
 *
 * Saved through admin-api, because `tt_optimizer_settings` has no write policy: dry run
 * must not be a station's to turn off. The catalogue comes from the mirror. Which of it is
 * bookable online comes from the proxy through admin-api, read again on each save. If that
 * can't be read, the page says so and the admin ticks the services by hand.
 *
 * The rules are ../rules/optimizer-settings.js.
 */
import * as Vue from 'vue';
import { api } from '../api.js';
import { nav } from '../nav.js';
import { store } from '../store.js';
import {
  buildPriorities, catalogueGroups, draftFromStored, emptyProblems, fieldProblems, priorityWarnings, toggleDraft
} from '../rules/optimizer-settings.js';

var DEFAULTS = { enabled: false, dry_run: true, check_minutes: 15, cutoff_minutes: 30 };

var Optimizer = {
  name: 'AdminOptimizer',
  setup: function () {
    var form = Vue.reactive(Object.assign({}, DEFAULTS));
    var checkInfo = Vue.ref(null);
    var services = Vue.ref([]);
    var names = Vue.ref({});
    var flags = Vue.ref(null);
    var flagsReason = Vue.ref(null);
    var draft = Vue.ref([]);
    var hand = Vue.ref({});
    var loaded = Vue.ref(false);
    var loadFailed = Vue.ref(false);
    var saving = Vue.ref(false);
    var problems = Vue.ref([]);
    var warnings = Vue.ref([]);
    // Set once the page has said the flags can't be read, so the ticks have been seen.
    var ticksSeen = Vue.ref(false);

    // Every await below can outlast the selection: this read is one upstream call per
    // technician. So nothing lands, and nothing is sent, for a salon no longer selected.
    function stale(id) { return id !== store.selectedId.value; }

    function applyCatalogue(res) {
      flags.value = (res && res.online) || null;
      flagsReason.value = flags.value ? null : ((res && res.reason) || 'unread');
      if (res && res.categories && Object.keys(res.categories).length) names.value = res.categories;
    }

    async function load() {
      var id = store.selectedId.value;
      loaded.value = false;
      loadFailed.value = false;
      problems.value = [];
      warnings.value = [];
      flags.value = null;
      flagsReason.value = null;
      names.value = {};
      if (!id) return;
      var res = await api.call('optimizer.settings.get', { tenantId: id });
      if (stale(id)) return;
      // No form without them: saving it would write defaults over what is there.
      if (!res) { loadFailed.value = true; loaded.value = true; return; }
      var cat = await api.rows('services', id, function (q) { return q.order('name'); });
      if (stale(id)) return;
      // Nor without the catalogue: a category's members come from it.
      if (!cat) { loadFailed.value = true; loaded.value = true; return; }
      var catalogue = await api.call('optimizer.catalogue', { tenantId: id });
      if (stale(id)) return;

      var row = res.settings || {};
      Object.assign(form, DEFAULTS, {
        enabled: row.enabled === true,
        dry_run: row.dry_run !== false,
        check_minutes: row.check_minutes || DEFAULTS.check_minutes,
        cutoff_minutes: row.cutoff_minutes == null ? DEFAULTS.cutoff_minutes : row.cutoff_minutes
      });
      checkInfo.value = { okAt: row.last_check_ok_at || null, error: row.last_check_error || null };
      var stored = draftFromStored(row.priorities);
      draft.value = stored.draft;
      hand.value = stored.hand;
      services.value = cat;
      applyCatalogue(catalogue);
      ticksSeen.value = !flags.value;
      loaded.value = true;
    }

    Vue.watch(store.selectedId, load, { immediate: true });

    var groups = Vue.computed(function () {
      return catalogueGroups(services.value, names.value, flags.value);
    });

    function isPicked(kind, refId) {
      var id = (kind === 'category' ? 'c:' : 's:') + refId;
      return draft.value.some(function (d) { return d.id === id; });
    }

    function pick(kind, refId, label) {
      draft.value = toggleDraft(draft.value, { kind: kind, refId: refId, label: label });
    }

    // With the flags unread, what the admin has to tick: what the picks come to.
    var handLines = Vue.computed(function () {
      if (flags.value) return [];
      var seen = {};
      var out = [];
      buildPriorities(draft.value, services.value, null, hand.value).forEach(function (p) {
        p.services.forEach(function (s) {
          if (seen[s.id]) return;
          seen[s.id] = true;
          var row = services.value.find(function (x) { return x.id === s.id; });
          out.push({ id: s.id, name: row ? row.name : 'Service ' + s.id });
        });
      });
      return out;
    });

    function setHand(id, on) {
      hand.value = Object.assign({}, hand.value, (function () { var o = {}; o[id] = on; return o; })());
    }

    async function save() {
      var id = store.selectedId.value;
      problems.value = fieldProblems(form);
      if (draft.value.some(function (d) { return !String(d.label || '').trim(); })) {
        problems.value = problems.value.concat(['Every priority needs a name.']);
      }
      if (problems.value.length) return;
      // What is on screen now is what is saved, whatever the form holds by the time the
      // read below comes back.
      var snapshot = {
        enabled: form.enabled,
        dry_run: form.dry_run,
        check_minutes: form.check_minutes,
        cutoff_minutes: form.cutoff_minutes
      };
      var picks = draft.value.map(function (d) { return Object.assign({}, d); });
      var ticks = Object.assign({}, hand.value);
      var catalogue = services.value;
      saving.value = true;
      try {
        var read = await api.call('optimizer.catalogue', { tenantId: id });
        if (stale(id)) return;
        applyCatalogue(read);
        // Newly unreadable: show the ticks before saving what they say.
        if (!flags.value && !ticksSeen.value) {
          ticksSeen.value = true;
          problems.value = ['Which services are bookable online can\'t be read just now. Tick them below, then save again.'];
          return;
        }
        var priorities = buildPriorities(picks, catalogue, flags.value, ticks);
        problems.value = emptyProblems(priorities);
        if (problems.value.length) return;
        var res = await api.call('optimizer.settings.save', Object.assign({
          tenantId: id,
          priorities: priorities
        }, snapshot));
        if (!res || stale(id)) return;
        warnings.value = priorityWarnings(priorities);
        ticksSeen.value = !flags.value;
        hand.value = draftFromStored(priorities).hand;
      } finally {
        saving.value = false;
      }
    }

    return {
      form: form,
      checkInfo: checkInfo,
      groups: groups,
      draft: draft,
      flags: flags,
      flagsReason: flagsReason,
      hand: hand,
      handLines: handLines,
      setHand: setHand,
      loaded: loaded,
      loadFailed: loadFailed,
      reload: load,
      saving: saving,
      problems: problems,
      warnings: warnings,
      isPicked: isPicked,
      pick: pick,
      save: save,
      stamp: store.stamp
    };
  },
  template: [
    '<div class="page">',
    '  <div class="page__head">',
    '    <h2>Optimizer</h2>',
    '    <p class="page__lead">Proposes technician changes that open up online booking for',
    '      services only one or two people can do. The desk approves each one.</p>',
    '  </div>',
    '  <p v-if="!loaded" class="empty">Loading…</p>',
    '  <div v-else-if="loadFailed" class="card card--warn">',
    '    <p>This salon&rsquo;s settings or catalogue couldn&rsquo;t be read, so there is nothing here to',
    '      save over them. Is schema part 24 applied?</p>',
    '    <div class="card__row"><button type="button" class="btn btn--ghost btn--sm" @click="reload()">Try again</button></div>',
    '  </div>',
    '  <template v-else>',
    '    <div class="card">',
    '      <div class="card__head"><h3>Running it</h3></div>',
    '      <label class="switch">',
    '        <input type="checkbox" v-model="form.enabled" />',
    '        <span class="switch__track"><span class="switch__thumb"></span></span>',
    '        <span class="switch__label">On</span>',
    '      </label>',
    '      <p class="hint">Off, nothing is checked, and the desk sees that it is off.</p>',
    '      <label class="switch">',
    '        <input type="checkbox" v-model="form.dry_run" />',
    '        <span class="switch__track"><span class="switch__thumb"></span></span>',
    '        <span class="switch__label">Dry run</span>',
    '      </label>',
    '      <p class="hint">On, approving goes through every check and saves nothing. Off, approving',
    '        really changes the technician on a client&rsquo;s booking in Mangomint.</p>',
    '      <div class="card__row">',
    '        <label class="field">',
    '          <span class="field__label">Check every (minutes)</span>',
    '          <input class="input" type="number" min="1" step="1" v-model.number="form.check_minutes" />',
    '        </label>',
    '        <label class="field">',
    '          <span class="field__label">Stop proposing this close to a booking (minutes)</span>',
    '          <input class="input" type="number" min="0" step="1" v-model.number="form.cutoff_minutes" />',
    '        </label>',
    '      </div>',
    '      <p class="hint">Scheduled checks run in opening hours only.</p>',
    '      <p v-if="checkInfo && checkInfo.okAt" class="hint">Last good check {{ stamp(checkInfo.okAt) }}.</p>',
    '      <p v-if="checkInfo && checkInfo.error" class="hint">Last check said: {{ checkInfo.error }}</p>',
    '    </div>',
    '',
    '    <div class="card">',
    '      <div class="card__head"><h3>Priorities</h3></div>',
    '      <p class="hint">The services and categories to open up. A category counts as scarce',
    '        when one or two people can do at least one of its services.</p>',
    '      <p class="hint">A service later taken offline in Mangomint stays a priority until this',
    '        page is saved again.</p>',
    '      <p v-if="flagsReason === \'wait\'" class="hint">Mangomint is asking to wait, so which services are',
    '        bookable online can&rsquo;t be read just now. Try again shortly, or tick them below.</p>',
    '      <p v-else-if="flagsReason" class="hint">Which services are bookable online can&rsquo;t be read',
    '        just now, so everything is listed. Tick the ones that are, below.</p>',
    '',
    '      <ul v-if="draft.length" class="opt-picked">',
    '        <li v-for="d in draft" :key="d.id" class="opt-picked__row">',
    '          <label class="field field--grow">',
    '            <span class="field__label">{{ d.kind === "category" ? "Category" : "Service" }}, as the desk reads it</span>',
    '            <input class="input" type="text" maxlength="80" v-model="d.label" />',
    '          </label>',
    '          <button type="button" class="btn btn--ghost btn--sm" @click="pick(d.kind, d.refId, d.label)">Remove</button>',
    '        </li>',
    '      </ul>',
    '      <p v-else class="hint"><strong>Nothing is chosen yet,</strong> so nothing will be proposed.</p>',
    '',
    '      <div v-if="handLines.length" class="opt-hand">',
    '        <p class="field__label">Bookable online</p>',
    '        <label v-for="s in handLines" :key="s.id" class="opt-hand__row">',
    '          <input type="checkbox" :checked="hand[s.id] === true" @change="setHand(s.id, $event.target.checked)" />',
    '          <span>{{ s.name }}</span>',
    '        </label>',
    '      </div>',
    '',
    '      <p v-if="!groups.length" class="empty">The catalogue is empty, or nothing in it is bookable online.</p>',
    '      <div v-for="g in groups" :key="g.key" class="opt-group">',
    '        <div class="opt-group__head">',
    '          <h4 class="opt-group__name">{{ g.label }}</h4>',
    '          <button v-if="g.categoryId != null" type="button" class="chip" :class="{ \'chip--on\': isPicked(\'category\', g.categoryId) }"',
    '            @click="pick(\'category\', g.categoryId, g.label)"><span class="chip__name">The whole category</span></button>',
    '        </div>',
    '        <div class="chips">',
    '          <button v-for="s in g.services" :key="s.id" type="button" class="chip"',
    '            :class="{ \'chip--on\': isPicked(\'service\', s.id) }" @click="pick(\'service\', s.id, s.name)">',
    '            <span class="chip__name">{{ s.name }}</span>',
    '          </button>',
    '        </div>',
    '      </div>',
    '    </div>',
    '',
    '    <p v-for="(p, i) in problems" :key="\'p\' + i" class="opt-problem">{{ p }}</p>',
    '    <p v-for="(w, i) in warnings" :key="\'w\' + i" class="hint">{{ w }}</p>',
    '    <div class="card__row">',
    '      <button type="button" class="btn btn--primary" :disabled="saving" @click="save">',
    '        {{ saving ? "Saving…" : "Save" }}</button>',
    '    </div>',
    '  </template>',
    '</div>'
  ].join('\n')
};

nav.register({
  id: 'optimizer',
  name: 'Optimizer',
  group: 'Connections',
  page: Optimizer
});

export { Optimizer };
